<template>
  <div v-if="showPopup" class="popup-fullscreen">
    <!-- 👇 내용 부분만 이미지 적용 -->
      <!-- 기존 popup-inner 를 감싸면서, 클릭 시 새 탭으로 이동 -->
      <div
        class="popup-inner"
        :style="backgroundStyle"
        @click="handleImageClick"          
        :class="{ clickable: popupData.linkUrl }"
      >
        <!-- 텍스트가 있을 때만 덮개 표시 -->
        <div
          v-if="isMeaningfulContent(popupData.content)"
          class="popup-content"
          :style="contentStyle"
          @click.stop              
        >
          <div v-html="popupData.content"></div>
        </div>
      </div>
    <!-- 👇 footer는 아래에 고정 -->
    <div class="popup-footer">
      <label class="left">
        <input type="checkbox" v-model="dontShowToday" />
        오늘 하루 보지 않기
      </label>
      <button class="right" @click="closePopup">닫기</button>
    </div>
  </div>
</template>




<script setup>
import { ref, onMounted, computed, watch } from 'vue';
import axios from '@/utils/axios';

const showPopup = ref(false);
const dontShowToday = ref(false);
const popupData = ref({
  id: null,
  title: '',
  content: '',
  imageUrl: '',
  linkUrl: '' 
});

const STORAGE_PREFIX = 'popup-hidden-';
const popupId = window.location.pathname.split("/").pop();
const STORAGE_KEY = `${STORAGE_PREFIX}${popupId}`;

function handleImageClick() {
  if (popupData.value.linkUrl) {
    window.open(popupData.value.linkUrl, '_blank', 'noopener');
    closePopup();
  }
}

// ✅ 팝업 크기 동적 설정
const contentStyle = ref({
  maxWidth: '100vw',
  maxHeight: '100vh',
  width: 'auto',
  height: 'auto',
});

function isMeaningfulContent(html) {
  if (!html) return false;
  // 모든 태그/공백 제거 후, 실제 내용이 남는지 체크
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim();
  return text.length > 0;
}


watch(
  () => popupData.value.imageUrl,
  () => {
    if (popupData.value.imageUrl) {
      const img = new window.Image();
      img.src = popupData.value.imageUrl;
      img.onload = () => {
        const padding = 120; // (footer, padding 등 여백 감안, 필요시 조절)
        let width = img.naturalWidth;
        let height = img.naturalHeight + padding;

        // 창 크기가 화면보다 크면 최대치 제한
        const maxWidth = window.screen.availWidth - 40;
        const maxHeight = window.screen.availHeight - 40;
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }
        if (height > maxHeight) {
          const ratio = maxHeight / height;
          height = maxHeight;
          width = width * ratio;
        }

        // 팝업창 자체 크기 조절
        window.resizeTo(Math.round(width), Math.round(height));

        // 내부 style도 맞추기
        contentStyle.value.width = `${Math.round(width)}px`;
        contentStyle.value.height = `${Math.round(height - padding)}px`;
      };
    }
  }
);


// ✅ 팝업 닫기
function closePopup() {
  if (dontShowToday.value) {
    const expireTime = new Date();
    expireTime.setHours(23, 59, 59, 999);
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ expires: expireTime.getTime() }));
  }

  showPopup.value = false;
  window.close();
}

// ✅ 저장된 localStorage 체크
function checkPopupHidden() {
  const data = localStorage.getItem(STORAGE_KEY);
  if (data) {
    try {
      const parsed = JSON.parse(data);
      const now = Date.now();
      if (parsed.expires && now < parsed.expires) {
        return true;
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
  }
  return false;
}

// ✅ 팝업 데이터 로딩
onMounted(async () => {
  if (!popupId) {
    return;
  }

  if (checkPopupHidden()) {
    return;
  }

  try {
    const res = await axios.get(`/api/bx201/popup/${popupId}`);
    popupData.value = res.data;
    document.title = res.data.title;
    showPopup.value = true;
  } catch (e) {
    showPopup.value = false;
  }
});

// ✅ 백그라운드 이미지 스타일
const backgroundStyle = computed(() => {
  return popupData.value.imageUrl
    ? {
        backgroundImage: `url(${popupData.value.imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        width: contentStyle.value.width,
        height: contentStyle.value.height,
      }
    : {};
});

</script>

<style scoped>
.popup-fullscreen {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white; /* fallback */
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 9999;
  border-radius: 10px;
  overflow: hidden;
  /* width/height는 JS에서 동적으로 지정됨 */
  /* padding-bottom: 0; 필요하면 추가 */
}

.clickable {
  cursor: pointer;
}

.popup-content {
  font-size: 1.2rem;
  text-align: center;
  color: #333;
  background-color: rgba(255, 255, 255, 0.95);
  padding: 20px 28px;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.10);
  margin: 0 auto;
}

/* 내용(텍스트/배경)은 위쪽 flex 1로 확장 */
.popup-inner {
  width: 100%;
  flex: 1 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  /* background 스타일은 바인딩으로 들어감 */
}
/* 푸터는 항상 아래에 */
.popup-footer {
  width: 100%;
  min-height: 52px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: rgba(250,248,255,0.93);
  border-top: 1px solid #eee;
  padding: 10px 24px;
  box-sizing: border-box;
}
.popup-footer .left {
  font-size: 0.98rem;
}
.popup-footer .right {
  font-size: 0.98rem;
  padding: 0.38rem 1.1rem;
  background: #6840e0;
  color: #fff;
  border-radius: 6px;
  border: none;
  cursor: pointer;
}
.popup-footer .right:hover {
  background: #4323b6;
}
</style>
